import { cn } from '../../lib/format.js';
import { useReducedMotion } from '../../hooks/useReducedMotion.js';

/**
 * A row that scrolls itself, forever.
 *
 * The track holds the same children twice and translates exactly -50%, so the
 * loop closes on itself with no visible jump — that is why it is a duplicate
 * rather than a clever modulo. The second run is `aria-hidden` and its contents
 * are taken out of the tab order, or a screen reader reads the whole row twice
 * and tabbing walks into copies nobody can see.
 *
 * `overflow-hidden` on the wrapper is load-bearing: the track is `w-max` and
 * wider than the viewport by design, and without it the document itself grows
 * sideways and every page carrying the rail gets a horizontal scrollbar.
 *
 * The edges fade with a mask rather than a gradient overlay, because an overlay
 * has to be painted in the page's background colour. The mask has its own limit
 * though: it dissolves to transparent, so it only reads as a soft edge over a
 * plain ground. Over a tint it looks like a smudge, which is why `fade` can be
 * turned off per instance rather than being assumed.
 *
 * It pauses on hover and on keyboard focus. A card sliding out from under a
 * focus ring is a trap, and someone who has moused over a row is reading it.
 * Under reduced motion nothing moves and it becomes an ordinary scrollable row.
 */

export const ContinuousRail = ({ items, renderItem, itemKey, speed = 54, fade = true, className, itemClassName }) => {
  const reduced = useReducedMotion();

  if (!items?.length) return null;

  return (
    <div
      className={cn(
        'group relative -mx-4 overflow-hidden sm:-mx-6',
        fade && !reduced && '[mask-image:linear-gradient(to_right,transparent,black_7%,black_93%,transparent)]',
        className,
      )}
    >
      <div
        className={cn(
          'flex',
          reduced
            ? // Must not be `w-max`: the row needs the container's width to have
              // something to scroll within.
              'gap-4 overflow-x-auto px-4 sm:px-6'
            : 'w-max rail-marquee',
        )}
        style={reduced ? undefined : { '--rail-duration': `${speed}s` }}
      >
        {[0, 1].map((run) => (
          <div
            key={run}
            aria-hidden={run === 1 ? 'true' : undefined}
            {...(run === 1 ? { inert: true } : {})}
            className={cn('flex shrink-0', reduced ? 'gap-4' : 'gap-4 px-2')}
          >
            {items.map((item, index) => (
              <div
                key={`${run}-${itemKey ? itemKey(item, index) : index}`}
                className={cn('shrink-0', itemClassName)}
              >
                {renderItem(item, index, run === 1)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContinuousRail;
